import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import { useSelector } from 'react-redux';
import useVisinaTabele from "../../custom/visinaTabele";
import './Kazne.css';

function formatDT(val) {
  if (!val) return '—';
  const s = String(val).replace('T', ' ').substring(0, 16);
  const [datePart, timePart] = s.split(' ');
  if (!datePart) return '—';
  const [y, mo, d] = datePart.split('-');
  return timePart ? `${d}.${mo}.${y} ${timePart}` : `${d}.${mo}.${y}`;
}

const ALL_COLS = [
  { field: 'Vozac',          header: 'Vozač' },
  { field: 'BrojKartice',    header: 'Broj kartice' },
  { field: 'AktivnostiOd',   header: 'Aktivnosti od' },
  { field: 'AktivnostiDo',   header: 'Aktivnosti do' },
  { field: 'DanaAktivnosti', header: 'Dana aktivnosti' },
  { field: 'DanaUSehngenu',  header: 'Dana u Schengenu (180 dana)' },
  { field: 'BezZemlje',      header: 'Bez zemlje (vremena)' },
];

const DATE_FIELDS = new Set(['AktivnostiOd', 'AktivnostiDo']);

const KazneEES = () => {
  const { t } = useTranslation();
  const toast   = useRef(null);
  const auth    = useContext(AuthContext);
  const company = useSelector(state => state.company);

  const [dateFrom,        setDateFrom]        = useState(new Date(Date.now() - 180 * 86400000));
  const [dateTo,          setDateTo]          = useState(new Date());
  const [tableData,       setTableData]       = useState([]);
  const [load,            setLoad]            = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(ALL_COLS.map(c => c.field));
  /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
  const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([tableData, selectedColumns]);
  const [globalFilter,    setGlobalFilter]    = useState(null);
  const [first,           setFirst]           = useState(0);

  const toISO = d => d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    : (d || '');

  const handleSearch = async () => {
    if (!dateFrom || !dateTo) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Unesite opseg datuma', life: 3000 });
      return;
    }
    setLoad(true);
    setTableData([]);
    try {
      const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/ees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
        body: JSON.stringify({ dateFrom: toISO(dateFrom), dateTo: toISO(dateTo) }),
      });
      const data = await r.json();
      if (r.ok) {
        setTableData(Array.isArray(data) ? data : []);
      } else {
        toast.current.show({ severity: 'error', summary: 'Greška', detail: data.message || 'Greška pri učitavanju', life: 4000 });
      }
    } catch {
      toast.current.show({ severity: 'error', summary: 'Greška', detail: 'Greška pri komunikaciji sa serverom', life: 4000 });
    }
    setLoad(false);
  };

  const rowClassName = (row) => (Number(row.DanaUSehngenu) > 90 ? 'row-red' : '');

  const visibleCols = ALL_COLS.filter(c => selectedColumns.includes(c.field));
  const colOptions  = ALL_COLS.map(c => ({ label: c.header, value: c.field }));

  const exportExcel = async () => {
    if (!tableData || tableData.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('EES Provera');
    const headers   = visibleCols.map(c => c.header);

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });

    tableData.forEach(rowData => {
      const row = worksheet.addRow(visibleCols.map(c => {
        const v = rowData[c.field];
        return DATE_FIELDS.has(c.field) ? formatDT(v) : (v ?? '');
      }));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });

    worksheet.columns.forEach((col, i) => {
      const field   = visibleCols[i]?.field;
      const lengths = [headers[i].length, ...tableData.map(r => String(r[field] ?? '').length)];
      col.width = Math.max(...lengths) + 2;
    });

    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `EES Provera - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`
    );
  };

  const header = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Tooltip target=".ees-xls-btn" position="bottom" />
        <Button
          type="button"
          icon="pi pi-file-excel"
          severity="success"
          rounded
          onClick={exportExcel}
          className="ees-xls-btn"
          data-pr-tooltip="Export XLS"
        />
      </div>
    </div>
  );

  return (
    <div id="zaposleni_main">
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="kazne-izv-filters">
        <div className="kazne-izv-filter-row">
          <label className="kazne-izv-label">{t('mk.odDatuma')}</label>
          <Calendar
            value={dateFrom}
            onChange={e => setDateFrom(e.value)}
            showIcon
            dateFormat="dd.mm.yy"
            mask="99.99.9999"
          />
          <label className="kazne-izv-label">{t('mk.doDatuma')}</label>
          <Calendar
            value={dateTo}
            onChange={e => setDateTo(e.value)}
            showIcon
            dateFormat="dd.mm.yy"
            mask="99.99.9999"
          />
          <Button
            label={t('mk.prikazi')}
            icon="pi pi-search"
            onClick={handleSearch}
            className="p-button p-button-primary"
          />
          <span className="kazne-ees-note">
            <i className="pi pi-info-circle" style={{ marginRight: 4 }} />
            Schengen dani se računaju za poslednjih 180 dana od danas
          </span>
        </div>
      </div>

      <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.8em', alignItems: 'center' }}>
          <MultiSelect
            value={selectedColumns}
            options={colOptions}
            onChange={e => {
              const sorted = ALL_COLS.map(c => c.field).filter(f => e.value.includes(f));
              setSelectedColumns(sorted);
            }}
            optionLabel="label"
            display="chip"
            placeholder={t('nova.odaberiteKolone')}
            style={{ width: 'fit-content', minWidth: '180px' }}
            showSelectAll
            filter
            filterPlaceholder="Pretraži kolone"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} kolona odabrano"
          />
          <input
            type="search"
            onInput={e => setGlobalFilter(e.target.value)}
            placeholder={t('nova.phPretraga')}
            className="p-inputtext p-component"
          />
        </div>

        <div ref={omotTabele}>
        <DataTable
          value={tableData}
          paginator
          rows={redovaTabele}
          rowsPerPageOptions={[redovaTabele, 50, 100]}
          first={first}
          onPage={e => setFirst(e.first)}
          globalFilter={globalFilter}
          scrollable
          scrollHeight={visinaTabele}
          emptyMessage={t('common.noDataTable')}
          resizableColumns
          columnResizeMode="expand"
          className="turePregledTable stavke-tabela"
          header={header}
          rowClassName={rowClassName}
        >
          {visibleCols.map(col => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              sortable
              style={{ minWidth: col.field === 'BezZemlje' ? '220px' : '140px' }}
              body={
                DATE_FIELDS.has(col.field)
                  ? r => formatDT(r[col.field])
                  : col.field === 'BezZemlje'
                    ? r => r.BezZemlje
                      ? <span style={{ color: '#b45309', fontSize: 'smaller' }}>{r.BezZemlje}</span>
                      : null
                    : undefined
              }
            />
          ))}
        </DataTable>
        </div>
      </div>
    </div>
  );
};

export default KazneEES;
